"use client";

import React, { useRef } from "react";
import { Box, Layers, Maximize2, ShieldCheck } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const trailerDimensions = [{
  title: "6 × 8 Trailer",
  tag: "Compact",
  exterior: "144″ L × 92″ W × 92″ H",
  interior: "89.5″ L × 65″ W × 62″ H"
}, {
  title: "7 × 12 Trailer",
  tag: "Mid-Size",
  exterior: "192″ L × 102″ W × 98″ H",
  interior: "144″ L × 74″ W × 72″ H"
}, {
  title: "7 × 16 Trailer",
  tag: "Large Towable",
  exterior: "240″ L × 102″ W × 92″ H",
  interior: "192″ L × 74″ W × 72″ H"
}, {
  title: "53-Foot Reefer Trailer",
  tag: "Typical Dimensions",
  exterior: "53′ L × 102.36″ W × 13′ 6″ H",
  interior: "Approximately 52′ 2″ L × 97.4″ W × 105.5″ H"
}];
export default function Specifications() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".reveal-construction", {
        y: 16
      });
      gsap.to(".reveal-construction", {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".reveal-dimension-card", {
        y: 20
      });
      gsap.to(".reveal-dimension-card", {
        opacity: 1,
        y: 0,
        duration: 0.5,
        stagger: 0.08,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".dimensions-grid",
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".reveal-construction", {
        opacity: 1,
        y: 0
      });
      gsap.set(".reveal-dimension-card", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="trailerconstructionanddimensions" data-nav="light" ref={containerRef} className="w-full bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Section Header */}
        <div className="mb-12 md:mb-16 text-center max-w-3xl mx-auto">
          <Badge variant="outline" className="mb-3 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Technical Specifications
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Towable Trailer Construction and Dimensions
          </h2>
          <p className="mt-4 text-muted-foreground text-base md:text-lg leading-relaxed">
            The construction details in this section apply to the 6×8, 7×12, and 7×16 trailers. Typical manufacturer dimensions for the 53-foot reefer trailer are displayed alongside verified towable trailer dimensions, while other 53-foot reefer specifications remain pending confirmation.
          </p>
        </div>

        {/* Part 1 — Construction */}
        <div className="mb-14 md:mb-18">
          <div className="reveal-construction bg-card text-card-foreground border border-border rounded-lg p-6 md:p-8 shadow-xs" style={{
          opacity: 0
        }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center size-10 rounded-md bg-accent text-accent-foreground">
                <ShieldCheck className="size-5" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-card-foreground">
                Trailer Construction
              </h3>
            </div>
            <Separator className="my-4" />
            <p className="text-muted-foreground leading-relaxed text-base md:text-lg">
              The trailers feature thick tubular aluminum main rails, aluminum angle cross members, an aluminum pallet-duty floor, NSF- and USDA-approved interior wall, ceiling, and floor materials, a 304 stainless-steel doorframe, EPDM door seals, and a tongue jack with foot.
            </p>
          </div>
        </div>

        {/* Part 2 — Dimensions */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center justify-center size-10 rounded-md bg-accent text-accent-foreground">
              <Maximize2 className="size-5" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-foreground">
              Trailer Dimensions
            </h3>
          </div>

          <div className="dimensions-grid grid grid-cols-1 md:grid-cols-2 gap-6">
            {trailerDimensions.map((item, i) => <div key={item.title} data-index={i} className="reveal-dimension-card h-full" style={{
            opacity: 0
          }}>
                <Card className="h-full border border-border rounded-lg bg-card shadow-xs transition-[border-color,box-shadow] hover:shadow-md hover:border-primary/40 flex flex-col justify-between">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-lg md:text-xl font-bold text-card-foreground">
                        {item.title}
                      </CardTitle>
                      <Badge variant="secondary" className="text-xs font-medium">
                        {item.tag}
                      </Badge>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-0">
                    <Separator />

                    {/* Exterior Spec Block */}
                    <div className="rounded-md bg-muted/50 p-3.5 border border-border/60">
                      <div className="flex items-center gap-2 mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <Box className="size-3.5" />
                        <span>Exterior</span>
                      </div>
                      <p className="text-sm md:text-base font-medium text-foreground tracking-tight break-words">
                        {item.exterior}
                      </p>
                    </div>

                    {/* Interior Spec Block */}
                    <div className="rounded-md bg-muted/50 p-3.5 border border-border/60">
                      <div className="flex items-center gap-2 mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <Layers className="size-3.5" />
                        <span>Interior</span>
                      </div>
                      <p className="text-sm md:text-base font-medium text-foreground tracking-tight break-words">
                        {item.interior}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>)}
          </div>

          <p className="text-xs md:text-sm text-muted-foreground leading-relaxed mt-4">
            Dimensions shown for the 53-foot reefer trailer are typical manufacturer dimensions. Exact measurements may vary by trailer configuration and are confirmed individually.
          </p>
        </div>
      </div>
    </section>;
}